// Shared context menu for entities (IOCs, CVEs, techniques, actors, families).
// Portal-rendered, reuses malpedia-context-menu CSS classes.

import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getVirusTotalUrl,
  getNvdUrl,
  getMitreAttackUrl,
} from '../features/ioc/iocDetailHelpers';
import { fetchExploreCategories } from '../lib/exploreApi';
import type { ExploreCategoriesResponse } from '../lib/exploreApi';
import { actorDetailPath, familyDetailPath } from '../lib/paths';
import { getActiveZoom } from '../lib/zoomPosition';

// Categories that get a detail page even though they are not in the IOC registry
// (e.g. CVE has its own detail logic; "ioc" is the legacy catch-all key;
// "actor" routes to `/actors/<name>`).
const NON_REGISTRY_DETAIL_CATEGORIES = new Set(['ioc', 'cve', 'actor', 'family']);

export interface EntityDescriptor {
  value: string;        // "CVE-2024-1234", "192.168.1.1", "APT29"
  category: string;     // "ioc" | "cve" | "technique" | "actor" | "family"
  subType?: string;     // IOC sub-type: "IP", "DOMAIN", "SHA256", etc.
  neo4jLabel?: string;  // override graph label if known
  attackId?: string;    // MITRE ATT&CK ID for techniques
}

interface EntityContextMenuProps {
  descriptor: EntityDescriptor;
  position: { x: number; y: number };
  onSearch?: () => void;
  onClose: () => void;
}

// Non-IOC entity types map to dedicated Neo4j labels; everything else is
// resolved against the registry (registry categories all live as :IOC nodes).
const CATEGORY_TO_LABEL: Record<string, string> = {
  cve: 'CVE',
  technique: 'AttackTechnique',
  actor: 'ThreatActor',
  family: 'MalwareFamily',
};

function categoryToNeo4jLabel(desc: EntityDescriptor): string {
  return desc.neo4jLabel || CATEGORY_TO_LABEL[desc.category] || 'IOC';
}

// VT-compatible IOC sub-types
const VT_TYPES = new Set(['IP', 'IPV6', 'DOMAIN', 'SHA256', 'SHA1', 'MD5', 'SHA512', 'URL']);

export default function EntityContextMenu({
  descriptor,
  position,
  onSearch,
  onClose,
}: EntityContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Clamp position to viewport (in unscaled CSS px; see getActiveZoom).
  const z = getActiveZoom();
  const style: React.CSSProperties = {
    top: Math.min(position.y / z, window.innerHeight / z - 260),
    left: Math.min(position.x / z, window.innerWidth / z - 200),
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const { value, category, subType, attackId } = descriptor;
  const label = categoryToNeo4jLabel(descriptor);
  const graphSeed = category === 'technique' && attackId ? attackId : value;

  const { data: catsData } = useQuery<ExploreCategoriesResponse>({
    queryKey: ['explore-categories'],
    queryFn: fetchExploreCategories,
    staleTime: 60_000,
  });
  const isRegistryCategory = (catsData?.groups ?? [])
    .some(g => g.categories.some(c => c.key === category));
  const hasDetail = isRegistryCategory || NON_REGISTRY_DETAIL_CATEGORIES.has(category);
  const vtUrl = subType && VT_TYPES.has(subType.toUpperCase())
    ? getVirusTotalUrl(subType, value)
    : null;
  const nvdUrl = category === 'cve' ? getNvdUrl(value) : null;
  const mitreUrl = category === 'technique' && attackId
    ? getMitreAttackUrl('MITRE_TECHNIQUE', attackId)
    : null;

  return ReactDOM.createPortal(
    <div ref={menuRef} className="malpedia-context-menu" style={style} role="menu">
      {hasDetail && (
        <button
          className="malpedia-context-menu-item"
          role="menuitem"
          onClick={() => {
            const detailUrl = category === 'actor'
              ? actorDetailPath(value)
              : category === 'family'
                ? familyDetailPath(value)
                : `/ioc/${encodeURIComponent(value)}`;
            navigate(detailUrl);
            onClose();
          }}
        >
          View Details
        </button>
      )}

      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => {
          navigate(`/graph?seed=${encodeURIComponent(graphSeed)}&label=${encodeURIComponent(label)}`);
          onClose();
        }}
      >
        View in Graph
      </button>

      {(vtUrl || nvdUrl || mitreUrl) && (
        <div className="malpedia-context-menu-divider" />
      )}

      {vtUrl && (
        <button
          className="malpedia-context-menu-item malpedia-context-menu-item--external"
          role="menuitem"
          onClick={() => { window.open(vtUrl, '_blank', 'noopener'); onClose(); }}
        >
          Open in VirusTotal
        </button>
      )}

      {nvdUrl && (
        <button
          className="malpedia-context-menu-item malpedia-context-menu-item--external"
          role="menuitem"
          onClick={() => { window.open(nvdUrl, '_blank', 'noopener'); onClose(); }}
        >
          Open in NVD
        </button>
      )}

      {mitreUrl && (
        <button
          className="malpedia-context-menu-item malpedia-context-menu-item--external"
          role="menuitem"
          onClick={() => { window.open(mitreUrl, '_blank', 'noopener'); onClose(); }}
        >
          Open in MITRE ATT&CK
        </button>
      )}

      <div className="malpedia-context-menu-divider" />

      {onSearch && (
        <button
          className="malpedia-context-menu-item"
          role="menuitem"
          onClick={() => { onSearch(); onClose(); }}
        >
          Search for this
        </button>
      )}

      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          toast.success('Copied');
          onClose();
        }}
      >
        Copy
      </button>
    </div>,
    document.body,
  );
}
